import ExcelJS from 'exceljs';
import {
  addHeaderRow,
  addTitleRow,
  downloadWorkbook,
  fill,
  FONT,
  NAVY,
  NAVY_SOFT,
  TEXT_DARK,
  TEXT_GRAY,
} from '../../../utils/excelStyleHelpers';
import { getMidLevelCategoryOptionGroups } from '../../orderhistory/categoryUtils';

/**
 * Builds the blank "custom ingredient template" the bulk import reads back
 * (server: services/supplierPriceFile/customIngredientTemplateParser.ts).
 *
 * Header labels here are the contract — the parser matches them (plus Dutch
 * variants) case-insensitively and ignores column order, so keep them in sync.
 * The Ingredients sheet ships EMPTY on purpose: examples live on the Instructions
 * sheet, where they can't be imported by accident.
 */

// Column order of the Ingredients sheet. Also drives the dropdown ranges below.
export const TEMPLATE_HEADERS = [
  'Supplier',
  'Product name',
  'Article ID',
  'Price ex VAT',
  'Amount',
  'Unit',
  'Pack size',
  'VAT %',
  'Category',
];

const COLUMN_WIDTHS = [22, 38, 16, 14, 10, 10, 16, 8, 26];

// Units the parser accepts in the Unit column (kg/l/cl are folded down to g/ml).
const UNIT_VALUES = ['g', 'kg', 'ml', 'cl', 'l', 'piece'];
const VAT_VALUES = ['9', '21'];

// Importable cells follow the parser's English-label contract, independently of the UI language.
const CATEGORY_VALUES = getMidLevelCategoryOptionGroups('en').flatMap(group => group.options.map(option => option.label));

// Rows 2..VALIDATION_LAST_ROW get the dropdowns — plenty for a bulk paste.
const VALIDATION_LAST_ROW = 1000;

const HOW_TO_ROWS: [string, string][] = [
  ['Supplier', 'Required. The supplier this product comes from. Different suppliers can be mixed in one file — rows are grouped per supplier on import.'],
  ['Product name', 'Required. The name you want to see in Cost Price and Inventory.'],
  ['Article ID', 'Optional. Leave empty if the supplier has no article number — an ID is generated from the product name.'],
  ['Price ex VAT', 'Required. The price EXCLUDING VAT for the whole pack you describe in Amount + Unit. "€ 42,50" and "42.50" both work.'],
  ['Amount', 'Required. How much is in that pack, as a number. 5 litres -> 5. A 700 ml bottle -> 700 (with Unit "ml") or 0,7 (with Unit "l").'],
  ['Unit', `Required. One of: ${UNIT_VALUES.join(', ')}. kg, l and cl are converted to g / ml automatically.`],
  ['Pack size', 'Optional fallback, only used when Amount + Unit are empty. Free text like "6 x 800 g" or "2 LTR".'],
  ['VAT %', 'Optional. 9 or 21. Empty means 9%.'],
  ['Category', 'Optional. Pick from the dropdown. Empty leaves the ingredient uncategorized.'],
];

const EXAMPLE_ROWS = [
  ['Sligro', 'Olijfolie extra vierge', '123456', '42,50', '5', 'l', '', '9', 'Oils & Fats'],
  ['Local farm', 'Eieren', '', '0,32', '1', 'piece', '', '9', 'Eggs & Dairy'],
  ['Sligro', 'Tomatenblik', '', '18,00', '', '', '6 x 800 g', '9', 'Canned & Preserved'],
  ['Kemme Wines', 'Rioja Reserva', 'RIO-01', '9,75', '750', 'ml', '', '21', 'Wine'],
];

function addNoteRow(ws: ExcelJS.Worksheet, label: string, text: string): void {
  const row = ws.addRow([label, text]);
  row.getCell(1).font = { name: FONT, size: 10, bold: true, color: { argb: TEXT_DARK } };
  row.getCell(2).font = { name: FONT, size: 10, color: { argb: TEXT_GRAY } };
  row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
  row.height = 28;
}

function buildIngredientsSheet(wb: ExcelJS.Workbook): void {
  const ws = wb.addWorksheet('Ingredients', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = COLUMN_WIDTHS.map(width => ({ width }));
  addHeaderRow(ws, TEMPLATE_HEADERS);

  // Dropdowns keep Unit / VAT / Category spelled the way the parser expects.
  // Category is a range reference because the inline-list form caps at 255 chars.
  for (let r = 2; r <= VALIDATION_LAST_ROW; r++) {
    ws.getCell(r, 6).dataValidation = {
      type: 'list', allowBlank: true, formulae: [`"${UNIT_VALUES.join(',')}"`],
    };
    ws.getCell(r, 8).dataValidation = {
      type: 'list', allowBlank: true, formulae: [`"${VAT_VALUES.join(',')}"`],
    };
    ws.getCell(r, 9).dataValidation = {
      type: 'list', allowBlank: true, formulae: [`Lists!$A$2:$A$${CATEGORY_VALUES.length + 1}`],
    };
  }
}

function buildInstructionsSheet(wb: ExcelJS.Workbook): void {
  const ws = wb.addWorksheet('Instructions');
  ws.columns = [{ width: 18 }, { width: 110 }];

  addTitleRow(ws, 'Custom ingredient template');
  addNoteRow(ws, '', 'Fill in one row per ingredient on the "Ingredients" sheet, then upload this file. Nothing here is imported — this sheet is reference only.');
  addNoteRow(ws, '', 'An ingredient that already exists for that supplier is left untouched and reported as skipped, so re-uploading never overwrites prices you already have.');
  ws.addRow([]);

  addTitleRow(ws, 'Columns');
  HOW_TO_ROWS.forEach(([label, text]) => addNoteRow(ws, label, text));
  ws.addRow([]);

  addTitleRow(ws, 'Examples');
  const headerRow = addHeaderRow(ws, TEMPLATE_HEADERS);
  headerRow.height = 18;
  EXAMPLE_ROWS.forEach(values => {
    const row = ws.addRow(values);
    for (let c = 1; c <= TEMPLATE_HEADERS.length; c++) {
      row.getCell(c).font = { name: FONT, size: 10, color: { argb: TEXT_DARK } };
      row.getCell(c).fill = fill(NAVY_SOFT);
    }
  });
  addNoteRow(ws, '', 'Row 1 = €42,50 for 5 litres. Row 2 = €0,32 per egg. Row 3 = €18,00 for 6 x 800 g (4800 g). Row 4 = €9,75 for a 750 ml bottle at 21% VAT.');
}

function buildListsSheet(wb: ExcelJS.Workbook): void {
  const ws = wb.addWorksheet('Lists');
  ws.columns = [{ width: 28 }, { width: 12 }, { width: 10 }];
  addHeaderRow(ws, ['Category', 'Unit', 'VAT %']);

  const rowCount = Math.max(CATEGORY_VALUES.length, UNIT_VALUES.length, VAT_VALUES.length);
  for (let i = 0; i < rowCount; i++) {
    const row = ws.addRow([CATEGORY_VALUES[i] ?? '', UNIT_VALUES[i] ?? '', VAT_VALUES[i] ?? '']);
    for (let c = 1; c <= 3; c++) {
      row.getCell(c).font = { name: FONT, size: 10, color: { argb: TEXT_DARK } };
    }
  }
  ws.getCell('A1').font = { name: FONT, size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  ws.getCell('A1').fill = fill(NAVY);
}

/**
 * Builds the blank template workbook. Exported separately from the download so the
 * server-side parser test can round-trip a filled-in copy of the real thing.
 */
export function buildCustomIngredientTemplateWorkbook(): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Culinary Cloud';
  buildIngredientsSheet(wb);
  buildInstructionsSheet(wb);
  buildListsSheet(wb);
  return wb;
}

/** Builds and downloads the blank custom-ingredient template. */
export async function downloadCustomIngredientTemplate(): Promise<void> {
  await downloadWorkbook(buildCustomIngredientTemplateWorkbook(), 'custom-ingredient-template.xlsx');
}
